/**
 * /embudos — tablero de oportunidades (Board Pattern del DESIGN_SYSTEM).
 *
 * Tres vistas: tablero, lista y el editor del embudo (pantalla propia, no
 * modal — no entra en --evo-dialog-w). El tablero y la lista comparten el
 * FilterRow y el filtro de estado; el editor no los muestra.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DndContext, DragOverlay, PointerSensor, pointerWithin,
  useSensor, useSensors, useDroppable, useDraggable,
  type DragEndEvent, type DragStartEvent,
} from '@dnd-kit/core';
import type { OpportunityStatus } from '@evo-hub/shared';
import { Board, BoardColumn, BoardCard, BoardEmpty } from '../components/ui/Board.js';
import { FilterRow } from '../components/ui/FilterRow.js';
import { SegmentedControl } from '../components/ui/SegmentedControl.js';
import { Dropdown } from '../components/ui/Dropdown.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { PillGroup, Pill } from '../components/PillGroup.js';
import { IconButton } from '../components/ui/IconButton.js';
import { Icon } from '../components/Icon.js';
import { Modal } from '../components/ui/Modal.js';
import { Button } from '../components/ui/Button.js';
import { Input } from '../components/ui/Input.js';
import { ContactSelector, type ContactSelectorValue } from '../components/ui/ContactSelector.js';
import { CreateEstimateModal } from '../components/billing/CreateEstimateModal.js';
import { CreateProjectModal } from '../components/projects/CreateProjectModal.js';
import { OpportunityPanel } from '../components/funnels/OpportunityPanel.js';
import { OpportunityClaudePanel } from '../components/funnels/OpportunityClaudePanel.js';
import { OpportunityList } from '../components/funnels/OpportunityList.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { useEsNuevo, useMarcarItemVisto } from '../contexts/NavSeenContext.js';
import { useToast } from '../components/ui/Toast.js';
import { formatMoney } from '../lib/money.js';
import { relTime } from '../lib/relTime.js';
import { matchesSearch } from '../lib/searchTerms.js';
import {
  useFunnels, useBoard, useCreateOpportunity,
  useMoveOpportunity, useDeleteOpportunity, useUpdateOpportunity,
  useOpportunityToContact, useOpportunityToEstimate,
  useBulkMoveOpportunities, useBulkOpportunityToContact,
  type Opportunity, type FunnelStageWithTotals,
} from '../hooks/useFunnels.js';

/** Más de 7 días en la misma etapa: la edad se pinta en ámbar. */
const STALE_DAYS = 7;

function isStale(stageEnteredAt: string): boolean {
  const iso = stageEnteredAt.endsWith('Z') ? stageEnteredAt : stageEnteredAt + 'Z';
  return Date.now() - new Date(iso).getTime() > STALE_DAYS * 86_400_000;
}

/** De dónde viene la carta, en una línea. */
function subtitleOf(o: Opportunity): string | undefined {
  if (o.estimateNumber) return [o.estimateNumber, o.contactName].filter(Boolean).join(' · ');
  if (o.projectName) return o.projectName;
  if (o.formName) return [o.contactName, o.formName].filter(Boolean).join(' · ') || o.formName;
  return o.contactName ?? undefined;
}

// ── Carta arrastrable ─────────────────────────────────────────────────────────

function DraggableCard({ opportunity, selected, nueva, onOpen }: {
  opportunity: Opportunity;
  selected: boolean;
  /** Entró desde la última visita al módulo. */
  nueva: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: opportunity.id });
  const toContact = useOpportunityToContact();
  const toEstimate = useOpportunityToEstimate();

  // Las dos acciones solo tienen sentido en una carta que vino de un
  // formulario: es de ahí de donde salen los datos del contacto y los
  // conceptos de la cotización.
  const desdeFormulario = opportunity.submissionId != null;

  function convertir() {
    toContact.mutate(opportunity.id, {
      onSuccess: r => toast.success(t(r.created ? 'funnels.card.contactCreated' : 'funnels.card.contactLinked')),
      onError: err => toast.error(err instanceof Error ? err.message : t('common.error')),
    });
  }

  function cotizar() {
    toEstimate.mutate(opportunity.id, {
      onSuccess: r => {
        toast.success(t('funnels.card.estimateReady'));
        navigate(`/facturacion/estimate/${r.estimateId}`);
      },
      onError: err => toast.error(err instanceof Error ? err.message : t('common.error')),
    });
  }

  return (
    // `touchAction: none` es lo que deja arrastrar con el dedo: sin eso el
    // navegador se queda el gesto para hacer scroll y el arrastre no arranca.
    <div ref={setNodeRef} {...listeners} {...attributes} style={{ touchAction: 'none' }}>
      <BoardCard
        title={opportunity.title}
        {...(subtitleOf(opportunity) != null ? { subtitle: subtitleOf(opportunity)! } : {})}
        value={formatMoney(opportunity.valueCents, opportunity.currency)}
        age={relTime(opportunity.stageEnteredAt, t as (k: string, opts?: Record<string, unknown>) => string)}
        ageTone={isStale(opportunity.stageEnteredAt) ? 'stale' : 'normal'}
        nueva={nueva}
        selected={selected}
        dragging={isDragging}
        onOpen={onOpen}
        pills={opportunity.contactId == null ? (
          <Pill variant="outline" tone="muted" size="xs">{t('opportunities.noContact')}</Pill>
        ) : undefined}
        actions={desdeFormulario ? (
          <>
            {opportunity.contactId == null && (
              <IconButton
                icon={<Icon name="contact-add" size={13} />}
                ariaLabel={t('funnels.card.toContact')}
                tooltip={t('funnels.card.toContact')}
                variant="ghost"
                size="sm"
                loading={toContact.isPending}
                onClick={convertir}
              />
            )}
            {opportunity.estimateId == null && (
              <IconButton
                icon={<Icon name="billing" size={13} />}
                ariaLabel={t('funnels.card.toEstimate')}
                tooltip={t('funnels.card.toEstimate')}
                variant="ghost"
                tone="cyan"
                size="sm"
                loading={toEstimate.isPending}
                onClick={cotizar}
              />
            )}
          </>
        ) : undefined}
      />
    </div>
  );
}

// ── Columna con zona de suelta ────────────────────────────────────────────────

function DroppableColumn({ stage, cards, selectedId, isEntry, esNueva, onOpen }: {
  stage: FunnelStageWithTotals;
  cards: Opportunity[];
  selectedId: number | null;
  /** Columna de entrada de un embudo con formulario vinculado: lleva el chip AUTO. */
  isEntry: boolean;
  esNueva: (o: Opportunity) => boolean;
  onOpen: (id: number) => void;
}) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });

  const dropLabel = stage.outcome === 'won' ? t('funnels.column.dropWon')
    : stage.outcome === 'lost' ? t('funnels.column.dropLost')
    : t('funnels.column.drop');

  return (
    <div ref={setNodeRef} style={{ display: 'flex' }}>
      <BoardColumn
        name={stage.name}
        count={cards.length}
        total={formatMoney(stage.totalCents, cards[0]?.currency ?? 'usd')}
        tone={stage.tone}
        isDropTarget={isOver}
        {...(isEntry ? { intakeLabel: t('funnels.column.auto') } : {})}
        {...(stage.outcome != null ? { outcomeLabel: t(`funnels.column.${stage.outcome}`) } : {})}
      >
        {cards.map(card => (
          <DraggableCard
            key={card.id}
            opportunity={card}
            selected={card.id === selectedId}
            nueva={esNueva(card)}
            onOpen={() => onOpen(card.id)}
          />
        ))}
        {cards.length === 0 && <BoardEmpty label={dropLabel} />}
      </BoardColumn>
    </div>
  );
}

// ── Alta de oportunidad ───────────────────────────────────────────────────────

function CreateOpportunityModal({ open, funnelId, onClose, onCreated }: {
  open: boolean;
  funnelId: number;
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const create = useCreateOpportunity();
  const [title, setTitle] = useState('');
  const [contact, setContact] = useState<ContactSelectorValue | null>(null);

  useEffect(() => {
    if (open) { setTitle(''); setContact(null); }
  }, [open]);

  async function submit() {
    try {
      const row = await create.mutateAsync({
        funnelId,
        title: title.trim(),
        contactId: contact?.id ?? null,
      });
      onClose();
      onCreated(row.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('funnels.newOpportunity')}
      size="sm"
      preventClose={create.isPending}
      footer={
        <>
          <Button variant="outline" tone="muted" onClick={onClose} disabled={create.isPending}>
            {t('common.cancel')}
          </Button>
          <Button tone="cyan" loading={create.isPending} disabled={!title.trim()} onClick={() => { void submit(); }}>
            {t('common.create')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
        <Input label={t('opportunities.label')} value={title} onChange={setTitle} fullWidth />
        <ContactSelector
          label={t('opportunities.field.contact')}
          value={contact}
          onChange={setContact}
          fullWidth
        />
      </div>
    </Modal>
  );
}

/** Vincula un contacto a una carta que llegó sin él (típico de la ingesta). */
function LinkContactModal({ opportunityId, onClose }: { opportunityId: number | null; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const update = useUpdateOpportunity();
  const [contact, setContact] = useState<ContactSelectorValue | null>(null);

  useEffect(() => { setContact(null); }, [opportunityId]);

  async function submit() {
    if (opportunityId == null || !contact) return;
    try {
      await update.mutateAsync({ id: opportunityId, contactId: contact.id });
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  }

  return (
    <Modal
      open={opportunityId != null}
      onClose={onClose}
      title={t('opportunities.link.contactAction')}
      size="sm"
      preventClose={update.isPending}
      footer={
        <>
          <Button variant="outline" tone="muted" onClick={onClose} disabled={update.isPending}>
            {t('common.cancel')}
          </Button>
          <Button tone="cyan" loading={update.isPending} disabled={!contact} onClick={() => { void submit(); }}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <ContactSelector
        label={t('opportunities.field.contact')}
        value={contact}
        onChange={setContact}
        fullWidth
      />
    </Modal>
  );
}

// ── Página ────────────────────────────────────────────────────────────────────

export function FunnelsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { setOverride } = useTopBarCtx();
  const { id } = useParams<{ id: string }>();

  const [view, setView] = useState<'board' | 'list'>('board');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<OpportunityStatus | ''>('open');
  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [showCreateOpportunity, setShowCreateOpportunity] = useState(false);
  const [linkContactFor, setLinkContactFor] = useState<number | null>(null);
  const [estimateFor, setEstimateFor] = useState<Opportunity | null>(null);
  const [projectFor, setProjectFor] = useState<Opportunity | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);

  const { data: funnelsData, isLoading: funnelsLoading } = useFunnels();
  const funnels = useMemo(() => funnelsData?.funnels ?? [], [funnelsData]);

  const funnelId = id != null ? Number(id) : null;

  // El filtro de estado es SOLO de la lista.
  //
  // En un tablero las columnas YA SON el estado: una carta que está en
  // "Aprobado" tiene status 'won' por definición. Filtrando por "Abiertas", la
  // columna Aprobado quedaba SIEMPRE vacía, y al soltar una carta ahí el
  // servidor le ponía won y desaparecía del tablero en el acto. Se veía como
  // que el arrastre perdía la carta; en realidad la estaba escondiendo el
  // propio filtro.
  const { data: board, isLoading: boardLoading } = useBoard(
    funnelId,
    view === 'list' ? (status || undefined) : undefined,
  );
  const move = useMoveOpportunity();
  const update = useUpdateOpportunity();
  const remove = useDeleteOpportunity();
  const bulkMove = useBulkMoveOpportunities();
  const bulkToContact = useBulkOpportunityToContact();

  const opportunities = useMemo(() => {
    const rows = board?.opportunities ?? [];
    // Por palabra y no por frase — lib/searchTerms.ts.
    return rows.filter(o => matchesSearch(search, [o.title, o.contactName ?? '', o.formName ?? '']));
  }, [board, search]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // Qué llegó desde la última vez. El contador del menú se apaga al entrar; sin
  // esto se apagaría también la única señal de CUÁLES eran (mismo error que
  // hubo en Reportes, PENDIENTES_CHICOS.md §1).
  const nuevoDesdeLaUltimaVisita = useEsNuevo('funnels');
  const marcarCartaVista = useMarcarItemVisto('funnels');
  const esNueva = (o: Opportunity) => nuevoDesdeLaUltimaVisita(o.createdAt, o.id);

  function abrirCarta(id: number) {
    marcarCartaVista(id);
    setSelectedCard(id);
  }

  useEffect(() => {
    setOverride({
      title: t('funnels.pageTitle'),
      heroIcon: 'funnel',
      heroIconColor: 'var(--evo-cyan)',
      bottomActions: (
        <PillGroup size="md">
          <Pill tone="default" icon="pencil" onClick={() => { if (funnelId != null) navigate(`/embudos/${funnelId}/editar`); }}>
            {t('funnels.editFunnel')}
          </Pill>
          <Pill tone="cyan" icon="plus" onClick={() => setShowCreateOpportunity(true)}>
            {t('funnels.newOpportunity')}
          </Pill>
        </PillGroup>
      ),
    });
    return () => setOverride({});
  }, [setOverride, t, funnelId, navigate]);

  const cartaArrastrada = draggingId != null
    ? opportunities.find(o => o.id === draggingId) ?? null
    : null;

  function handleDragStart(event: DragStartEvent) {
    setDraggingId(Number(event.active.id));
  }

  function handleDragEnd(event: DragEndEvent) {
    setDraggingId(null);
    if (!event.over) return;
    const id = Number(event.active.id);
    const stageId = Number(event.over.id);
    const card = opportunities.find(o => o.id === id);
    if (!card || card.stageId === stageId) return;
    // Movimiento optimista: react-query ya repinta con el invalidate del
    // onSettled; si el PATCH falla, la carta vuelve sola y se avisa.
    move.mutate({ id, stageId, index: 0 }, {
      onError: () => toast.error(t('opportunities.moveFailed')),
    });
  }

  function toggleAllRows() {
    // Marca o desmarca lo que se está VIENDO, no el embudo entero: con la
    // búsqueda puesta, "todos" tiene que significar los que están en pantalla.
    const visibles = opportunities.map(o => o.id);
    setSelectedRows(prev =>
      visibles.every(id => prev.has(id)) ? new Set() : new Set(visibles));
  }

  function toggleRow(id: number) {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function bulkDelete() {
    const ids = Array.from(selectedRows);
    await Promise.allSettled(ids.map(id => remove.mutateAsync(id)));
    setSelectedRows(new Set());
  }

  if (funnelsLoading) return <LoadingState />;

  const stages = board?.stages ?? [];
  // De qué formulario come este embudo — lo dice la lista, que es donde vive
  // el vínculo. Sin formulario no hay ingesta y la primera columna es una
  // columna más.
  const tieneFormulario = (funnels.find(f => f.id === funnelId)?.forms.length ?? 0) > 0;

  return (
    <div className="flex flex-col h-full animate-fade-blur-in">
      <FilterRow>
        <FilterRow.Dropdown<number>
          value={funnelId}
          options={funnels.map(f => ({ value: f.id, label: f.name }))}
          onChange={v => navigate(`/embudos/${v}`)}
        />
        <FilterRow.Search
          value={search}
          onChange={setSearch}
          placeholder={t('funnels.searchPlaceholder')}
        />
        {/* Un control que no hace nada en la vista donde se lo ve es peor que
            no tenerlo: en el tablero el estado lo dice la columna. */}
        {view === 'list' && (
          <FilterRow.Dropdown<string>
            value={status}
            placeholder={t('funnels.statusFilter.all')}
            options={[
              { value: '', label: t('funnels.statusFilter.all') },
              { value: 'open', label: t('funnels.statusFilter.open') },
              { value: 'won', label: t('funnels.statusFilter.won') },
              { value: 'lost', label: t('funnels.statusFilter.lost') },
            ]}
            onChange={v => setStatus(v as OpportunityStatus | '')}
          />
        )}
        <FilterRow.Spacer />
        <SegmentedControl<'board' | 'list'>
          value={view}
          onChange={setView}
          variant="subtle"
          options={[
            { value: 'board', label: t('funnels.view.board'), icon: 'board' },
            { value: 'list', label: t('funnels.view.list'), icon: 'list-bullet' },
          ]}
        />
      </FilterRow>

      {/* SelectionActionBar a page root, full-width, post-FilterRow (REGLA #7).
          El tablero no lleva cotejos — la selección múltiple es de la lista. */}
      <SelectionActionBar
        visible={selectedRows.size > 0}
        countLabel={t('common.selectedCount', { count: selectedRows.size })}
        onClearSelection={() => setSelectedRows(new Set())}
      >
        <Dropdown<number>
          value={null}
          placeholder={t('opportunities.moveStage')}
          options={stages.map(s => ({ value: s.id, label: s.name }))}
          onChange={stageId => {
            if (selectedRows.size === 0) return;
            bulkMove.mutate({ ids: Array.from(selectedRows), stageId }, {
              onSuccess: () => setSelectedRows(new Set()),
              onError: () => toast.error(t('opportunities.moveFailed')),
            });
          }}
          size="sm"
        />
        <SelectionActionBar.Action
          label={t('funnels.card.toContact')}
          tone="primary"
          loading={bulkToContact.isPending}
          onClick={() => {
            bulkToContact.mutate(Array.from(selectedRows), {
              onSuccess: r => {
                setSelectedRows(new Set());
                // Se dicen las tres cosas por separado. De cuarenta cartas,
                // algunas ya tenían contacto y otras se crearon a mano: un
                // "listo" que tape doce fallas es peor que no ofrecer la acción.
                toast.success(t('funnels.card.bulkContactDone', {
                  created: r.created, linked: r.linked, skipped: r.skipped,
                }));
              },
              onError: err => toast.error(err instanceof Error ? err.message : t('common.error')),
            });
          }}
        />
        <SelectionActionBar.Divider />
        <SelectionActionBar.Action
          label={t('common.delete')}
          tone="danger"
          loading={remove.isPending}
          onClick={() => { void bulkDelete(); }}
          confirmRequired
        />
      </SelectionActionBar>

      {/* Evo Brain: no dibuja nada acá, registra qué carta está abierta para que
          el dock sepa de cuál le hablan. */}
      <OpportunityClaudePanel opportunityId={selectedCard} funnelId={funnelId} />

      <div className="flex-1 overflow-hidden" style={{ display: 'flex', minHeight: 0 }}>
        {boardLoading ? (
          <LoadingState />
        ) : view === 'board' ? (
          <DndContext
            sensors={sensors}
            // Por PUNTERO y no por rectángulos. El default (rectIntersection)
            // compara el rect del elemento arrastrado contra el de cada
            // columna; como la carta original no se mueve de su lugar —lo que
            // viaja es la copia del DragOverlay—, ese rect nunca cambia y el
            // destino se calculaba siempre contra la columna de origen. Ese era
            // el bug: la carta desaparecía y no llegaba a ninguna parte.
            collisionDetection={pointerWithin}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setDraggingId(null)}
          >
            <Board>
              {stages.map((stage, index) => (
                <DroppableColumn
                  key={stage.id}
                  stage={stage}
                  cards={opportunities.filter(o => o.stageId === stage.id)}
                  selectedId={selectedCard}
                  isEntry={index === 0 && tieneFormulario}
                  esNueva={esNueva}
                  onOpen={abrirCarta}
                />
              ))}
            </Board>

            {/* La copia que sigue al cursor. Sin esto el arrastre es a ciegas:
                se ve el hueco donde estaba la carta y nada más. */}
            <DragOverlay dropAnimation={null}>
              {cartaArrastrada && (
                <div style={{ width: 'var(--evo-board-col-w)', cursor: 'grabbing' }}>
                  <BoardCard
                    title={cartaArrastrada.title}
                    {...(subtitleOf(cartaArrastrada) != null ? { subtitle: subtitleOf(cartaArrastrada)! } : {})}
                    value={formatMoney(cartaArrastrada.valueCents, cartaArrastrada.currency)}
                    arrastrando
                    onOpen={() => undefined}
                  />
                </div>
              )}
            </DragOverlay>
          </DndContext>
        ) : (
          <OpportunityList
            opportunities={opportunities}
            stages={stages}
            selected={selectedRows}
            onToggleSelect={toggleRow}
            onToggleAll={toggleAllRows}
            esNueva={esNueva}
            onOpen={abrirCarta}
          />
        )}

        {selectedCard != null && (
          <OpportunityPanel
            opportunityId={selectedCard}
            onClose={() => setSelectedCard(null)}
            onLinkContact={setLinkContactFor}
            onCreateEstimate={id => setEstimateFor(opportunities.find(o => o.id === id) ?? null)}
            onCreateProject={id => setProjectFor(opportunities.find(o => o.id === id) ?? null)}
          />
        )}
      </div>

      {funnelId != null && (
        <CreateOpportunityModal
          open={showCreateOpportunity}
          funnelId={funnelId}
          onClose={() => setShowCreateOpportunity(false)}
          onCreated={setSelectedCard}
        />
      )}

      <LinkContactModal opportunityId={linkContactFor} onClose={() => setLinkContactFor(null)} />

      {/* La cotización y el proyecto se crean con los modales que ya existen y
          se enganchan a la carta al volver — no hay un alta paralela. */}
      <CreateEstimateModal
        open={estimateFor != null}
        onClose={() => setEstimateFor(null)}
        preselectedContact={estimateFor?.contactId != null
          ? { id: estimateFor.contactId, name: estimateFor.contactName ?? '' }
          : null}
        onCreated={estimateId => {
          const card = estimateFor;
          setEstimateFor(null);
          if (card) update.mutate({ id: card.id, estimateId });
          navigate(`/facturacion/estimate/${estimateId}`);
        }}
      />

      <CreateProjectModal
        open={projectFor != null}
        onClose={() => setProjectFor(null)}
        preselectedContact={projectFor?.contactId != null
          ? { id: projectFor.contactId, name: projectFor.contactName ?? '' }
          : null}
      />
    </div>
  );
}
